// src/pages/NewsDetail.tsx
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { news, CATEGORY_LABELS } from '@/data/mock/news';
import { NewsCard } from '@/components/NewsCard';
import { 
  Calendar, 
  User, 
  ArrowLeft,
  Clock,
  Eye,
  Share2,
  Facebook,
  Twitter,
  Linkedin,
  Link2,
  Bookmark,
  ThumbsUp,
  MessageSquare,
  Printer,
  Mail,
  ChevronRight,
  TrendingUp,
  Tag,
  CheckCircle2
} from 'lucide-react';
import { X, ChevronLeft } from 'lucide-react';
import { motion, AnimatePresence, useScroll, useSpring } from 'framer-motion';
import { useState, useEffect } from 'react';

const NewsDetail = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [likes, setLikes] = useState(Math.floor(Math.random() * 100) + 50);
  const [isLiked, setIsLiked] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);

  const newsItem = news.find(n => n.slug === slug);
  const categoryLabel = newsItem
    ? CATEGORY_LABELS[newsItem.category]?.[isRTL ? 'ar' : 'en'] ?? newsItem.category
    : '';

  // Lightbox keyboard navigation
  useEffect(() => {
    if (lightbox === null || !newsItem) return;
    const count = newsItem.gallery.length;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (e.key === 'ArrowRight') setLightbox(i => (i === null ? i : (i + 1) % count));
      else if (e.key === 'ArrowLeft') setLightbox(i => (i === null ? i : (i - 1 + count) % count));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, newsItem]);
  // Calculate read time
  const readTime = newsItem 
    ? Math.max(3, Math.ceil((newsItem.content.en.length + newsItem.excerpt.en.length) / 200))
    : 3;

  // Related news (mock - same category)
  const relatedNews = news
    .filter(n => n.category === newsItem?.category && n.slug !== slug)
    .slice(0, 3);

  // Handle share
  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = newsItem?.title[isRTL ? 'ar' : 'en'] || '';
    
    const shareUrls: Record<string, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  // Copy link
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    // Show toast notification
  };

  if (!newsItem) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-surface-1">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-muted mb-6">
            <MessageSquare className="h-12 w-12 text-muted-foreground" />
          </div>
          <h1 className="text-3xl font-bold mb-4">{t('notFound.title')}</h1>
          <p className="text-muted-foreground mb-6">
            {isRTL ? 'الخبر غير موجود' : 'News article not found'}
          </p>
          <Link to="/news">
            <Button size="lg" className="btn-glow">
              <ArrowLeft className={`h-5 w-5 ${isRTL ? 'rotate-180 ml-2' : 'mr-2'}`} />
              {t('common.back')}
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">

      {/* Enhanced Page Hero */}
      <PageHero
        title={newsItem.title[isRTL ? 'ar' : 'en']}
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.news'), path: '/news' },
          { label: newsItem.title[isRTL ? 'ar' : 'en'] }
        ]}
        backgroundImage={newsItem.image}
      />

      {/* Back Button */}
      <section className="py-8 bg-background border-b border-border sticky top-0 z-40 backdrop-blur-xl bg-background/80">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Link to="/news">
              <Button variant="ghost" size="lg" className="group">
                <ArrowLeft className={`h-5 w-5 ${isRTL ? 'rotate-180 ml-2' : 'mr-2'} transition-transform group-hover:-translate-x-1`} />
                {isRTL ? 'العودة إلى الأخبار' : 'Back to News'}
              </Button>
            </Link>

            {/* Share Buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsBookmarked(!isBookmarked)}
                className={isBookmarked ? 'bg-primary/10 border-primary text-primary' : ''}
              >
                <Bookmark className={`h-5 w-5 ${isBookmarked ? 'fill-current' : ''}`} />
              </Button>
              <Button variant="outline" size="icon" onClick={() => window.print()}>
                <Printer className="h-5 w-5" />
              </Button>
              <Button variant="outline" size="icon" onClick={handleCopyLink}>
                <Link2 className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-12 gap-12">
              {/* Sidebar - Social Share (Desktop) */}
              <motion.aside
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="hidden lg:block lg:col-span-1"
              >
                <div className="sticky top-32">
                  <div className="flex flex-col gap-4">
                    {/* Like Button */}
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => {
                        setIsLiked(!isLiked);
                        setLikes(prev => isLiked ? prev - 1 : prev + 1);
                      }}
                      className={`p-3 rounded-xl border transition-all duration-300 ${
                        isLiked 
                          ? 'bg-primary/10 border-primary text-primary' 
                          : 'bg-card border-border hover:border-primary/50'
                      }`}
                    >
                      <ThumbsUp className={`h-5 w-5 ${isLiked ? 'fill-current' : ''}`} />
                    </motion.button>
                    <span className="text-center text-sm font-semibold">{likes}</span>

                    {/* Share Buttons */}
                    <div className="h-px bg-border my-2" />
                    
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleShare('facebook')}
                      className="hover:bg-blue-500/10 hover:border-blue-500 hover:text-blue-500"
                    >
                      <Facebook className="h-5 w-5" />
                    </Button>

                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleShare('twitter')}
                      className="hover:bg-sky-500/10 hover:border-sky-500 hover:text-sky-500"
                    >
                      <Twitter className="h-5 w-5" />
                    </Button>

                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleShare('linkedin')}
                      className="hover:bg-blue-600/10 hover:border-blue-600 hover:text-blue-600"
                    >
                      <Linkedin className="h-5 w-5" />
                    </Button>

                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopyLink}
                      className="hover:bg-primary/10 hover:border-primary hover:text-primary"
                    >
                      <Share2 className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </motion.aside>

              {/* Main Article */}
              <div className="lg:col-span-11">
                <motion.article
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  className="bg-card border border-border rounded-3xl overflow-hidden shadow-2xl"
                >
                  {/* Featured Image */}
                  <div className="relative h-[500px] bg-muted overflow-hidden">
                    <motion.img
                      initial={{ scale: 1.1 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.7 }}
                      src={newsItem.image}
                      alt={newsItem.title[isRTL ? 'ar' : 'en']}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />

                    {/* Category Badge */}
                    <div className={`absolute top-8 ${isRTL ? 'right-8' : 'left-8'}`}>
                      <div className="px-6 py-3 rounded-full bg-primary/95 backdrop-blur-sm border border-primary/40 shadow-lg">
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-primary-foreground" />
                          <span className="text-sm font-semibold text-primary-foreground">
                            {categoryLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Article Content */}
                  <div className="p-8 md:p-12">
                    {/* Meta Information */}
                    <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground mb-8 pb-8 border-b border-border">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                          <Calendar className="h-4 w-4 text-primary" />
                        </div>
                        <span className="font-medium">
                          {new Date(newsItem.date).toLocaleDateString(
                            isRTL ? 'ar-EG' : 'en-US',
                            { year: 'numeric', month: 'long', day: 'numeric' }
                          )}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <span className="font-medium">{newsItem.author}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                          <Clock className="h-4 w-4 text-primary" />
                        </div>
                        <span className="font-medium">
                          {readTime} {isRTL ? 'دقائق قراءة' : 'min read'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                          <Eye className="h-4 w-4 text-primary" />
                        </div>
                        <span className="font-medium">
                          {Math.floor(Math.random() * 1000) + 500} {isRTL ? 'مشاهدة' : 'views'}
                        </span>
                      </div>
                    </div>

                    {/* Title */}
                    <h1 className="text-4xl md:text-5xl font-bold mb-8 leading-tight">
                      {newsItem.title[isRTL ? 'ar' : 'en']}
                    </h1>

                    {/* Excerpt */}
                    <div className="p-6 rounded-2xl bg-primary/5 border-l-4 border-primary mb-10">
                      <p className="text-xl text-foreground leading-relaxed font-medium">
                        {newsItem.excerpt[isRTL ? 'ar' : 'en']}
                      </p>
                    </div>

                    {/* Content */}
                    <div className={`prose prose-lg max-w-none dark:prose-invert ${isRTL ? 'text-right' : ''}`}>
                      <div className="text-foreground/90 leading-relaxed text-lg space-y-6 whitespace-pre-line">
                        {newsItem.content[isRTL ? 'ar' : 'en']}
                      </div>
                    </div>

                    {/* Photo Gallery */}
                    {newsItem.gallery && newsItem.gallery.length > 0 && (
                      <div className="mt-12 pt-8 border-t border-border">
                        <div className="flex items-center gap-2 mb-6">
                          <Eye className="h-5 w-5 text-primary" />
                          <h3 className="text-2xl font-bold">
                            {isRTL ? 'معرض الصور' : 'Photo Gallery'}
                          </h3>
                          <span className="text-sm text-muted-foreground">
                            ({newsItem.gallery.length})
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {newsItem.gallery.map((src, idx) => (
                            <motion.button
                              key={src}
                              type="button"
                              onClick={() => setLightbox(idx)}
                              initial={{ opacity: 0, scale: 0.95 }}
                              whileInView={{ opacity: 1, scale: 1 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.4, delay: (idx % 6) * 0.05 }}
                              whileHover={{ scale: 1.03 }}
                              className="group relative aspect-square overflow-hidden rounded-2xl border border-border bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                              <img
                                src={src}
                                alt={`${newsItem.title[isRTL ? 'ar' : 'en']} — ${idx + 1}`}
                                loading="lazy"
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              />
                              <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-colors duration-300" />
                            </motion.button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    <div className="mt-12 pt-8 border-t border-border">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-sm font-semibold text-muted-foreground">
                          {isRTL ? 'الوسوم:' : 'Tags:'}
                        </span>
                        {['Champions Academy', categoryLabel, 'Sports', 'Gaza'].map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm font-medium text-primary hover:bg-primary/20 transition-colors cursor-pointer"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Author Card */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="mt-12 p-8 rounded-2xl bg-surface-1 border border-border"
                    >
                      <div className="flex items-start gap-6">
                        <div className="flex-shrink-0">
                          <div className="w-20 h-20 rounded-full bg-primary/10 border-2 border-primary/20 flex items-center justify-center">
                            <User className="h-10 w-10 text-primary" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold mb-2">{newsItem.author}</h3>
                          <p className="text-muted-foreground mb-4">
                            {isRTL
                              ? 'كاتب ومحرر رياضي في أكاديمية تشامبيونز'
                              : 'Sports Writer and Editor at Champions Academy'
                            }
                          </p>
                          <Button variant="outline" size="sm">
                            <Mail className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            {isRTL ? 'تواصل مع الكاتب' : 'Contact Author'}
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </motion.article>

                {/* Mobile Share Buttons */}
                <div className="lg:hidden mt-8">
                  <div className="p-6 rounded-2xl bg-card border border-border">
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Share2 className="h-5 w-5 text-primary" />
                      {isRTL ? 'شارك هذا الخبر' : 'Share this article'}
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="outline"
                        onClick={() => handleShare('facebook')}
                        className="flex-1 hover:bg-blue-500/10 hover:border-blue-500 hover:text-blue-500"
                      >
                        <Facebook className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        Facebook
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleShare('twitter')}
                        className="flex-1 hover:bg-sky-500/10 hover:border-sky-500 hover:text-sky-500"
                      >
                        <Twitter className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        Twitter
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleShare('linkedin')}
                        className="flex-1 hover:bg-blue-600/10 hover:border-blue-600 hover:text-blue-600"
                      >
                        <Linkedin className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        LinkedIn
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related News Section */}
      {relatedNews.length > 0 && (
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  <span className="text-sm font-semibold text-primary">
                    {isRTL ? 'أخبار ذات صلة' : 'Related News'}
                  </span>
                </div>

                <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
                  {isRTL ? 'قد يهمك أيضاً' : 'You May Also Like'}
                </h2>

                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  {isRTL
                    ? 'اكتشف المزيد من الأخبار المشابهة'
                    : 'Discover more related news articles'
                  }
                </p>
              </motion.div>

              {/* Related News Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedNews.map((item, idx) => (
                  <NewsCard
                    key={item.id}
                    slug={item.slug}
                    title={item.title[isRTL ? 'ar' : 'en']}
                    excerpt={item.excerpt[isRTL ? 'ar' : 'en']}
                    image={item.image}
                    date={item.date}
                    category={CATEGORY_LABELS[item.category]?.[isRTL ? 'ar' : 'en'] ?? item.category}
                    delay={idx * 0.1}
                    isRTL={isRTL}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background relative overflow-hidden">
        <motion.div
          animate={{
            backgroundPosition: ["0% 50%", "100% 50%"],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 bg-[length:200%_100%]"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Mail className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'ابقَ على اطلاع' : 'Stay Updated'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'لا تفوت أي خبر' : "Don't Miss Any News"}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              {isRTL
                ? 'اشترك في نشرتنا الإخبارية واحصل على آخر الأخبار مباشرة'
                : 'Subscribe to our newsletter and get the latest news directly'
              }
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-xl mx-auto">
              <Link to="/news" className="flex-1">
                <Button size="lg" className="w-full btn-glow px-12 py-7 text-lg rounded-full">
                  <TrendingUp className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'تصفح جميع الأخبار' : 'Browse All News'}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          >
            {/* Close */}
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute top-6 right-6 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              aria-label={isRTL ? 'إغلاق' : 'Close'}
            >
              <X className="h-6 w-6" />
            </button>

            {/* Counter */}
            <div className="absolute top-6 left-6 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium">
              {lightbox + 1} / {newsItem.gallery.length}
            </div>

            {/* Prev */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(i => (i === null ? i : (i - 1 + newsItem.gallery.length) % newsItem.gallery.length));
              }}
              className="absolute left-4 md:left-8 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              aria-label={isRTL ? 'السابق' : 'Previous'}
            >
              <ChevronLeft className="h-7 w-7" />
            </button>

            {/* Next */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox(i => (i === null ? i : (i + 1) % newsItem.gallery.length));
              }}
              className="absolute right-4 md:right-8 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              aria-label={isRTL ? 'التالي' : 'Next'}
            >
              <ChevronRight className="h-7 w-7" />
            </button>

            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
              src={newsItem.gallery[lightbox]}
              alt={`${newsItem.title[isRTL ? 'ar' : 'en']} — ${lightbox + 1}`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-2xl shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NewsDetail;
